import { useState } from 'react';
import { useActiveBrand } from '../../api/brand';
import {
  AlertCircle,
  BookOpen,
  CheckCircle2,
  Download,
  HelpCircle,
  Loader2,
  Phone,
  RefreshCw,
  Server,
} from 'lucide-react';
import { Button } from '../../components/Button';
import { Modal } from '../../components/Modal';
import { useCurrentReseller } from '../../api/reseller';

/**
 * Database unreachable — §8.3
 * Self-host only. Includes a Retry button + status info + diagnostic checklist.
 */
export function DbUnreachable() {
  const brand = useActiveBrand();
  const [retrying, setRetrying] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [guideOpen, setGuideOpen] = useState(false);
  const { data: r } = useCurrentReseller();
  if (!r) return null;

  function retry() {
    setRetrying(true);
    setTimeout(() => {
      setRetrying(false);
      setRetryCount((c) => c + 1);
    }, 1400);
  }

  return (
    <div className="min-h-screen bg-marketing dark:bg-marketing-dark flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-xl">
        <div className="text-center mb-8">
          <span className="inline-flex w-14 h-14 grid place-items-center rounded-full bg-rose-100 dark:bg-rose-900/40 text-rose-600 dark:text-rose-400 mb-4">
            <AlertCircle className="w-7 h-7" />
          </span>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
            Database unreachable
          </h1>
          <p className="mt-3 text-sm text-zinc-600 dark:text-zinc-300 max-w-md mx-auto">
            We can't reach your database server right now. Your data is safe — only access is
            interrupted.
          </p>
        </div>

        <div className="rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-950 overflow-hidden mb-5">
          <div className="px-5 sm:px-6 py-4 border-b border-zinc-200 dark:border-zinc-700">
            <h2 className="text-sm font-semibold text-zinc-950 dark:text-zinc-50 flex items-center gap-2">
              <Server className="w-4 h-4" />
              Connection details
            </h2>
          </div>
          <dl className="px-5 sm:px-6 py-4 space-y-3 text-sm">
            <Row
              label="Server"
              value={<span className="mono text-xs">pos-db.your-shop.com:3306</span>}
            />
            <Row
              label="Last successful connection"
              value={
                <span className="tabular-nums">
                  14:08, 03 May 2026{' '}
                  <span className="text-zinc-400 dark:text-zinc-500">(38 minutes ago)</span>
                </span>
              }
            />
            <Row
              label="Error"
              value={
                <span className="text-rose-700 dark:text-rose-400 mono text-xs">
                  ECONNREFUSED — No route to host
                </span>
              }
            />
          </dl>
        </div>

        <div className="rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-950 overflow-hidden mb-6">
          <div className="px-5 sm:px-6 py-4 border-b border-zinc-200 dark:border-zinc-700">
            <h2 className="text-sm font-semibold text-zinc-950 dark:text-zinc-50">
              What to check
            </h2>
          </div>
          <ul className="px-5 sm:px-6 py-4 space-y-2 text-sm text-zinc-700 dark:text-zinc-200">
            <li className="flex items-start gap-2">
              <span className="text-zinc-400 mt-0.5">·</span>
              Is your MySQL server running?
            </li>
            <li className="flex items-start gap-2">
              <span className="text-zinc-400 mt-0.5">·</span>
              Is the public host/IP still the same?
            </li>
            <li className="flex items-start gap-2">
              <span className="text-zinc-400 mt-0.5">·</span>
              Has TLS expired or rotated?
            </li>
            <li className="flex items-start gap-2">
              <span className="text-zinc-400 mt-0.5">·</span>
              Is your firewall blocking traffic from{' '}
              <span className="mono text-xs">52.22.18.0/24</span>?
            </li>
          </ul>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
          <Button
            size="lg"
            onClick={retry}
            iconLeft={
              retrying ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )
            }
            disabled={retrying}
          >
            {retrying
              ? 'Retrying…'
              : retryCount > 0
              ? `Retry (${retryCount + 1})`
              : 'Retry connection'}
          </Button>
          <Button
            variant="secondary"
            size="lg"
            onClick={() => setGuideOpen(true)}
            iconLeft={<BookOpen className="w-4 h-4" />}
          >
            View setup guide
          </Button>
          <a
            href={`tel:${r.supportPhone}`}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md text-base font-medium text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
          >
            <Phone className="w-4 h-4" />
            <span>Contact support</span>
          </a>
        </div>

        {retryCount > 0 && !retrying && (
          <p className="mt-5 text-xs text-rose-600 dark:text-rose-400 flex items-center gap-1.5">
            <AlertCircle className="w-3.5 h-3.5" />
            Still unreachable as of last attempt — please review the checklist above.
          </p>
        )}

        <p className="mt-8 text-xs text-zinc-500 dark:text-zinc-400 flex items-center justify-center gap-1.5">
          <HelpCircle className="w-3.5 h-3.5" />
          Status:{' '}
          <a
            href={`https://status.${brand.domain}`}
            className="text-brand dark:text-brand-dm hover:underline"
          >
            status.{brand.domain}
          </a>{' '}
          · No platform-wide incidents reported.
        </p>
      </div>

      <SetupGuideModal open={guideOpen} onClose={() => setGuideOpen(false)} />
    </div>
  );
}

function SetupGuideModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data: r } = useCurrentReseller();
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Self-hosted MySQL setup guide"
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          <Button
            iconLeft={<Download className="w-4 h-4" />}
            onClick={() => {
              alert('schema-bundle.sql download (mock — would stream the 114-table schema)');
            }}
          >
            Download schema bundle
          </Button>
        </>
      }
    >
      <div className="space-y-5 text-sm">
        <p className="text-zinc-700 dark:text-zinc-200">
          Your business is configured with self-hosted MySQL. We connect to your server from our
          infrastructure (IP range <span className="mono text-xs">52.22.18.0/24</span>) over TLS.
          Use the steps below to make sure your server is reachable and configured correctly.
        </p>

        <Section number={1} title="MySQL server requirements">
          <ul className="space-y-1.5 text-zinc-600 dark:text-zinc-300">
            <Bullet>MySQL 5.7+ or MariaDB 10.3+</Bullet>
            <Bullet>InnoDB engine, utf8mb4 charset</Bullet>
            <Bullet>At least 2 GB RAM allocated to the DB process</Bullet>
            <Bullet>Persistent disk with daily backups (you manage these)</Bullet>
          </ul>
        </Section>

        <Section number={2} title="Network — open port 3306 to our IPs">
          <p className="text-zinc-600 dark:text-zinc-300 mb-2">
            On your firewall, allow inbound TCP from this range to port 3306:
          </p>
          <pre className="bg-zinc-900 text-zinc-100 rounded-md p-3 text-xs mono overflow-x-auto">
{`# Linux iptables example
sudo iptables -A INPUT -p tcp -s 52.22.18.0/24 --dport 3306 -j ACCEPT

# UFW
sudo ufw allow from 52.22.18.0/24 to any port 3306 proto tcp`}
          </pre>
        </Section>

        <Section number={3} title="TLS — required, not optional">
          <p className="text-zinc-600 dark:text-zinc-300 mb-2">
            Your MySQL must accept TLS connections. Generate or install a cert
            (Let's Encrypt works fine), then in <span className="mono text-xs">my.cnf</span>:
          </p>
          <pre className="bg-zinc-900 text-zinc-100 rounded-md p-3 text-xs mono overflow-x-auto">
{`[mysqld]
require_secure_transport = ON
ssl-cert = /etc/mysql/server-cert.pem
ssl-key  = /etc/mysql/server-key.pem
ssl-ca   = /etc/mysql/ca-cert.pem`}
          </pre>
          <p className="text-zinc-600 dark:text-zinc-300 mt-2">
            We refuse to connect over plaintext. Self-signed certs are accepted; we pin to your
            server's cert fingerprint after first successful connection.
          </p>
        </Section>

        <Section number={4} title="Database user — least privilege">
          <p className="text-zinc-600 dark:text-zinc-300 mb-2">
            Create a dedicated user for our app. Grant only what's needed:
          </p>
          <pre className="bg-zinc-900 text-zinc-100 rounded-md p-3 text-xs mono overflow-x-auto">
{`CREATE USER 'pos_app'@'%' IDENTIFIED BY 'strong-password' REQUIRE SSL;
GRANT ALL PRIVILEGES ON your_pos_db.* TO 'pos_app'@'%';
FLUSH PRIVILEGES;`}
          </pre>
          <p className="text-zinc-600 dark:text-zinc-300 mt-2">
            Avoid grants like <span className="mono text-xs">*.*</span> — scope to your POS
            database only.
          </p>
        </Section>

        <Section number={5} title="Apply the schema bundle">
          <p className="text-zinc-600 dark:text-zinc-300 mb-2">
            Download the schema bundle (button below) and run it once against your empty database:
          </p>
          <pre className="bg-zinc-900 text-zinc-100 rounded-md p-3 text-xs mono overflow-x-auto">
{`mysql -h localhost -u root -p your_pos_db < schema-bundle.sql`}
          </pre>
          <p className="text-zinc-600 dark:text-zinc-300 mt-2">
            Already done at signup? Re-running the bundle is idempotent — it skips existing
            tables. The bundle creates 114 tables and seeds default rows (currencies, permissions,
            invoice scheme, default theme, BD timezone).
          </p>
        </Section>

        <Section number={6} title="Common ECONNREFUSED reasons">
          <ul className="space-y-1.5 text-zinc-600 dark:text-zinc-300">
            <Bullet>MySQL bound to <span className="mono text-xs">127.0.0.1</span> only — set <span className="mono text-xs">bind-address = 0.0.0.0</span> in <span className="mono text-xs">my.cnf</span></Bullet>
            <Bullet>Cloud-provider security group not updated to allow our IP range</Bullet>
            <Bullet>Server moved to a new IP / DNS not yet propagated — update us at <span className="mono text-xs">{r?.supportEmail ?? ''}</span></Bullet>
            <Bullet>TLS cert expired — Let's Encrypt certs renew silently with auto-renew but check the renewal log</Bullet>
            <Bullet>SELinux / AppArmor denying outbound MySQL connections — check audit log</Bullet>
          </ul>
        </Section>

        <div className="rounded-md bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900 p-3 flex items-start gap-2.5">
          <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
          <span className="text-xs text-emerald-900 dark:text-emerald-100">
            Once your server is reachable, click <strong>Retry connection</strong> on the
            previous screen. We resume automatically — no further action required.
          </span>
        </div>
      </div>
    </Modal>
  );
}

function Section({
  number,
  title,
  children,
}: {
  number: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-zinc-950 dark:text-zinc-50 flex items-center gap-2 mb-2">
        <span className="inline-flex w-5 h-5 grid place-items-center rounded-full bg-brand text-white dark:bg-brand-dm dark:text-zinc-950 text-[11px] font-bold tabular-nums">
          {number}
        </span>
        {title}
      </h3>
      <div className="pl-7">{children}</div>
    </div>
  );
}

function Bullet({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2">
      <span className="text-zinc-400 dark:text-zinc-500 mt-0.5 flex-shrink-0">·</span>
      <span>{children}</span>
    </li>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <dt className="text-zinc-500 dark:text-zinc-400 text-xs uppercase tracking-wide flex-shrink-0">
        {label}
      </dt>
      <dd className="text-right text-zinc-900 dark:text-zinc-100 break-all">{value}</dd>
    </div>
  );
}
